import React, { useEffect, useState, useMemo } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import Card from './components/Card';
import Loader from './components/Loader';
import Table from './components/Table';
import SearchHighlightBanner from './components/SearchHighlightBanner';
import ReferencePagesBanner from './components/ReferencePagesBanner';
import ReferencePageBadge from './components/ReferencePageBadge';
import { getTechnicalErrors } from './api';
import {
  AlertTriangle, Download, ExternalLink, CheckCircle2,
  Search, X,
} from 'lucide-react';
import TechnicalErrorsGrouped from './components/TechnicalErrorsGrouped';
import {
  SEV_CONFIG,
  typeLabel,
  flattenTechnicalErrors,
  partitionTechnicalErrors,
  normStr,
  matchesHighlight,
} from './utils/technicalErrors';
import { translateError, effortInfo } from './utils/auditDictionary';

const COLUMNS = [
  { key: 'severity', label: 'Priorité', className: 'w-[100px]' },
  { key: 'category', label: 'Problème', className: 'min-w-[160px]' },
  { key: 'impact', label: 'Impact business', className: 'min-w-[200px]' },
  { key: 'url', label: 'Page', className: 'min-w-[180px]' },
  { key: 'type', label: 'Domaine', className: 'w-[100px]' },
  { key: 'recommendation', label: 'À corriger', className: 'min-w-[180px]' },
  { key: 'effort', label: 'Effort', className: 'w-[88px]' },
];

export default function ProjectTechnicalErrors() {
  const { project } = useOutletContext();
  const location = useLocation();
  const highlight = location.state?.searchHighlight || null;

  const [errorsData, setErrorsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(highlight || '');

  useEffect(() => {
    if (!project?.slug) return;
    setLoading(true);
    setFetchError(null);
    getTechnicalErrors(project.slug)
      .then((data) => setErrorsData(data))
      .catch((err) =>
        setFetchError(err.response?.data?.detail || err.message || 'Erreur de chargement')
      )
      .finally(() => setLoading(false));
  }, [project?.slug]);

  const allRows = useMemo(
    () => flattenTechnicalErrors(errorsData, project),
    [errorsData, project]
  );

  const { common: commonGroups, specific: specificRows } = useMemo(
    () => partitionTechnicalErrors(allRows),
    [allRows],
  );

  const filteredRows = useMemo(() => {
    const q = normStr(searchQuery);
    return allRows.filter((row) => {
      if (severityFilter !== 'all' && row.severity !== severityFilter) return false;
      if (typeFilter !== 'all' && row.type !== typeFilter) return false;
      if (!q) return true;
      return (
        normStr(row.category).includes(q) ||
        normStr(row.message).includes(q) ||
        normStr(row.recommendation).includes(q) ||
        normStr(row.url).includes(q) ||
        normStr(typeLabel(row.type)).includes(q)
      );
    });
  }, [allRows, severityFilter, typeFilter, searchQuery]);

  const filteredCommon = useMemo(() => {
    const q = normStr(searchQuery);
    return commonGroups.filter((g) => {
      if (severityFilter !== 'all' && g.severity !== severityFilter) return false;
      if (typeFilter !== 'all' && g.type !== typeFilter) return false;
      if (!q) return true;
      return (
        normStr(g.category).includes(q) ||
        normStr(g.translation?.pourquoi).includes(q) ||
        normStr(g.recommendation).includes(q) ||
        g.urls.some((u) => normStr(u).includes(q))
      );
    });
  }, [commonGroups, severityFilter, typeFilter, searchQuery]);

  const filteredSpecific = useMemo(() => {
    const q = normStr(searchQuery);
    return specificRows.filter((row) => {
      if (severityFilter !== 'all' && row.severity !== severityFilter) return false;
      if (typeFilter !== 'all' && row.type !== typeFilter) return false;
      if (!q) return true;
      return (
        normStr(row.category).includes(q) ||
        normStr(row.message).includes(q) ||
        normStr(row.recommendation).includes(q) ||
        normStr(row.url).includes(q) ||
        normStr(typeLabel(row.type)).includes(q)
      );
    });
  }, [specificRows, severityFilter, typeFilter, searchQuery]);

  if (loading) return <Loader className="h-96" label="Chargement des erreurs techniques…" />;

  if (fetchError) {
    return (
      <Card>
        <div className="text-center py-8">
          <AlertTriangle className="text-red-400 mx-auto mb-3" size={36} />
          <div className="text-red-600 font-semibold mb-1">Erreur de chargement</div>
          <div className="text-neutral-500 text-sm">{fetchError}</div>
        </div>
      </Card>
    );
  }

  if (!errorsData || errorsData.total_pages_with_errors === 0) {
    return (
      <div className="flex flex-col gap-5">
        <PageIntro />
        <Card>
          <div className="text-center py-16">
            <CheckCircle2 className="text-green-500 mx-auto mb-4" size={52} />
            <div className="text-neutral-900 font-semibold text-lg mb-1">Aucun problème détecté</div>
            <div className="text-neutral-400 text-sm max-w-md mx-auto">
              Les pages analysées ne présentent pas d&apos;alerte technique prioritaire.
              Le détail SEO page par page reste disponible dans l&apos;onglet URL &amp; SEO.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const sev = errorsData.errors_by_severity || {};
  const types = Object.entries(errorsData.errors_by_type || {});

  return (
    <div className="flex flex-col gap-5">
      <SearchHighlightBanner />
      <ReferencePagesBanner project={project} />
      <PageIntro />

      {/* Synthèse */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard
          label="Problèmes au total"
          value={errorsData.total_errors || 0}
          sub={`sur ${errorsData.total_pages_with_errors} page${errorsData.total_pages_with_errors > 1 ? 's' : ''}`}
        />
        {[
          { key: 'high', count: sev.high || 0 },
          { key: 'medium', count: sev.medium || 0 },
          { key: 'low', count: sev.low || 0 },
        ].map(({ key, count }) => {
          const cfg = SEV_CONFIG[key];
          const active = severityFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSeverityFilter((f) => (f === key ? 'all' : key))}
              className={`text-left rounded-xl border p-4 transition ${cfg.counter} ${
                active ? 'ring-2 ring-offset-1 ring-current' : 'hover:opacity-90'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                <span className="text-xs font-semibold uppercase tracking-wide">{cfg.label}</span>
              </div>
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-[11px] opacity-70 mt-0.5">
                {active ? 'Filtre actif · recliquer pour tout voir' : 'Cliquer pour filtrer'}
              </div>
            </button>
          );
        })}
      </div>

      {/* Filtres */}
      <Card className="p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un problème, une page, une action…"
              className="w-full pl-9 pr-8 py-2.5 text-sm border border-blue-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 bg-white"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-gray-300 hover:text-blue-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setTypeFilter('all')}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                typeFilter === 'all'
                  ? 'bg-accent text-white border-accent'
                  : 'bg-white text-blue-gray-600 border-blue-gray-200 hover:border-accent/40'
              }`}
            >
              Tous les domaines
            </button>
            {types.map(([type, count]) => (
              <button
                key={type}
                type="button"
                onClick={() => setTypeFilter((f) => (f === type ? 'all' : type))}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  typeFilter === type
                    ? 'bg-accent text-white border-accent'
                    : 'bg-white text-blue-gray-600 border-blue-gray-200 hover:border-accent/40'
                }`}
              >
                {typeLabel(type)} <span className="opacity-70 ml-0.5">{count}</span>
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-blue-gray-400 mt-3">
          {filteredRows.length} ligne{filteredRows.length !== 1 ? 's' : ''} affichée{filteredRows.length !== 1 ? 's' : ''}
          {(severityFilter !== 'all' || typeFilter !== 'all' || searchQuery) && ' (filtres actifs)'}
        </p>
      </Card>

      {filteredCommon.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-blue-gray-800 mb-1">
            Problèmes communs ({filteredCommon.length})
          </h3>
          <p className="text-xs text-blue-gray-500 mb-4">
            Même correction sur plusieurs pages — souvent à traiter une fois (thème, serveur, configuration globale).
          </p>
          <TechnicalErrorsGrouped groups={filteredCommon} />
        </Card>
      )}

      {/* Problèmes spécifiques (une page) */}
      {filteredSpecific.length === 0 && filteredCommon.length === 0 ? (
        <Card>
          <div className="text-center py-10 text-blue-gray-400 text-sm">
            Aucun problème ne correspond à vos filtres.
          </div>
        </Card>
      ) : filteredSpecific.length > 0 ? (
        <Table
          caption={filteredCommon.length > 0 ? 'Problèmes propres à une page' : 'Liste des problèmes techniques à corriger'}
          columns={COLUMNS}
          rows={filteredSpecific}
          getRowClassName={(row) => {
            const hl = highlight && matchesHighlight(row.url, highlight);
            if (hl) return 'bg-accent/10 ring-1 ring-inset ring-accent/25';
            if (row.isReference) return 'bg-accent/[0.04]';
            return '';
          }}
          renderCell={(row, col) => {
            const cfg = SEV_CONFIG[row.severity] || SEV_CONFIG.low;

            if (col.key === 'severity') {
              return (
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.badge}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </span>
              );
            }

            if (col.key === 'category') {
              const showDetail = row.message && normStr(row.message) !== normStr(row.category);
              return (
                <div className="min-w-0">
                  <p className="font-medium text-blue-gray-800 leading-snug">{row.category}</p>
                  {showDetail && (
                    <p className="text-[11px] text-blue-gray-400 mt-0.5 leading-snug line-clamp-2">{row.message}</p>
                  )}
                </div>
              );
            }

            if (col.key === 'impact') {
              const t = translateError(row);
              return (
                <p className="text-xs text-blue-gray-600 leading-relaxed line-clamp-4" title={t.pourquoi}>
                  {t.pourquoi || '—'}
                </p>
              );
            }

            if (col.key === 'url') {
              const short = row.url.replace(/^https?:\/\//, '');
              return (
                <div className="flex items-center gap-1.5 min-w-0 max-w-[280px]">
                  <a
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:underline truncate text-sm flex items-center gap-1 min-w-0"
                    title={row.url}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={11} className="shrink-0" />
                    <span className="truncate">{short}</span>
                  </a>
                  {row.isReference && <ReferencePageBadge compact />}
                </div>
              );
            }

            if (col.key === 'type') {
              return (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-gray-100 text-blue-gray-600 whitespace-nowrap">
                  {typeLabel(row.type)}
                </span>
              );
            }

            if (col.key === 'recommendation') {
              return row.recommendation ? (
                <p className="text-xs text-blue-gray-600 leading-relaxed line-clamp-3" title={row.recommendation}>
                  {row.recommendation}
                </p>
              ) : (
                <span className="text-blue-gray-300 text-xs">—</span>
              );
            }

            if (col.key === 'effort') {
              const eff = effortInfo(translateError(row).effort);
              return (
                <span
                  className="text-[10px] font-medium text-blue-gray-600 bg-blue-gray-50 border border-blue-gray-200 px-1.5 py-0.5 rounded whitespace-nowrap"
                  title={eff.hint}
                >
                  {eff.label}
                </span>
              );
            }

            return null;
          }}
        />
      ) : null}
    </div>
  );
}

function PageIntro() {
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-blue-gray-900 mb-1">Points à corriger</h1>
      <p className="text-sm text-blue-gray-600 max-w-3xl leading-relaxed">
        Liste priorisée de tous les problèmes détectés sur vos pages auditées.
        Chaque ligne indique <strong className="font-medium text-blue-gray-700">ce qui ne va pas</strong>,{' '}
        <strong className="font-medium text-blue-gray-700">sur quelle page</strong> et{' '}
        <strong className="font-medium text-blue-gray-700">quoi faire</strong>.
        Le détail des balises SEO se trouve dans l&apos;onglet URL &amp; SEO.
      </p>
    </div>
  );
}

function SummaryCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-blue-gray-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-gray-400 mb-1">{label}</div>
      <div className="text-2xl font-bold text-blue-gray-900">{value}</div>
      {sub && <div className="text-xs text-blue-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}
